"use client"

import { ArrowLeft, Shield, Eye, Database, Globe, Lock, UserCheck, Bell, Mail, FileText, Server, Trash2 } from "lucide-react"
import Link from "next/link"

export default function PrivacyPolicyPage() {
  const lastUpdated = "March 1, 2026"
  const effectiveDate = "March 1, 2026"

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border dark:border-white/[0.06] bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/auth/signin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-6 h-6" />
            <span className="font-semibold tracking-wide uppercase text-sm hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            <span className="font-semibold tracking-wide uppercase text-sm inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
              <p className="text-muted-foreground text-sm">How FluxPay collects, uses, and protects your information</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Last Updated: {lastUpdated}</span>
            <span>|</span>
            <span>Effective: {effectiveDate}</span>
          </div>
        </div>

        {/* GDPR/CCPA Notice */}
        <div className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold mb-2">Your Privacy Rights</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                FluxPay is committed to protecting your privacy and complying with applicable data protection regulations, including the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), the Personal Information Protection and Electronic Documents Act (PIPEDA), and other applicable privacy laws. You have specific rights regarding your personal data, which are detailed in this policy.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: "Information We Collect", href: "#collect" },
              { label: "How We Use Data", href: "#use" },
              { label: "Data Sharing", href: "#sharing" },
              { label: "Data Security", href: "#security" },
              { label: "Data Retention", href: "#retention" },
              { label: "Your Rights", href: "#rights" },
              { label: "Lawful Basis", href: "#use" },
              { label: "Cookies", href: "#cookies" },
              { label: "International Transfers", href: "#transfers" },
              { label: "Contact Us", href: "#contact" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 rounded-lg bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] text-sm text-muted-foreground hover:text-foreground hover:border-cyan-500/30 transition-all"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        {/* Legal Content */}
        <div className="prose prose-invert prose-gray max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">Introduction</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                FluxPay, a trade name of Ideation House Financial Services Limited (CBCA Corporation No. 1419582-5) ("FluxPay," "we," "us," or "our") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our payment processing services, website, dashboard, APIs, and related technologies (collectively, the "Services").
              </p>
              <p>
                By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our Services.
              </p>
              <p>
                For the purposes of applicable data protection laws, including the GDPR, FluxPay is the data controller responsible for your personal data.
              </p>
            </div>
          </section>

          {/* Section 1: Information We Collect */}
          <section id="collect" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-400" />
              1. Information We Collect
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">1.1 Information You Provide Directly</h3>
              <p>We collect information you voluntarily provide when using our Services, including:</p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Category</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Examples</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Account Information</td>
                      <td className="py-3 px-4">Name, email address, phone number, password, company name</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Business Information</td>
                      <td className="py-3 px-4">Business registration details, tax identification numbers, business address, industry type, website URL</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Financial Information</td>
                      <td className="py-3 px-4">Bank account details, payment card information (processed securely), billing address</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Identity Verification</td>
                      <td className="py-3 px-4">Government-issued ID, proof of address, beneficial owner information</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Communications</td>
                      <td className="py-3 px-4">Support tickets, emails, chat messages, feedback</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">1.2 Information Collected Automatically</h3>
              <p>When you use our Services, we automatically collect certain information, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers, screen resolution</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on pages, click patterns, search queries</li>
                <li><strong>Transaction Data:</strong> Payment amounts, timestamps, merchant and customer identifiers, transaction status</li>
                <li><strong>Log Data:</strong> Access times, error logs, referring URLs, API calls</li>
                <li><strong>Location Data:</strong> General geographic location based on IP address</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">1.3 Information from Third Parties</h3>
              <p>We may receive information about you from third parties, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Identity Verification Services:</strong> Results of background checks and identity verification</li>
                <li><strong>Credit Bureaus:</strong> Credit reports and business credit information</li>
                <li><strong>Payment Networks:</strong> Transaction data from Visa, Mastercard, and other card networks</li>
                <li><strong>Business Partners:</strong> Referral information from partners and affiliates</li>
                <li><strong>Public Sources:</strong> Publicly available information from business registries and databases</li>
              </ul>
            </div>
          </section>

          {/* Section 2: How We Use Your Information */}
          <section id="use" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-400" />
              2. How We Use Your Information
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>We use the information we collect for the following purposes:</p>

              <h3 className="text-lg font-medium text-foreground">2.1 Service Delivery</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Process payment transactions and settlements</li>
                <li>Provide access to merchant dashboard and reporting tools</li>
                <li>Manage your account and provide customer support</li>
                <li>Send transactional notifications and service updates</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.2 Security and Fraud Prevention</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Detect, prevent, and investigate fraudulent transactions</li>
                <li>Verify your identity and conduct due diligence</li>
                <li>Monitor for suspicious activity and security threats</li>
                <li>Comply with anti-money laundering (AML) requirements</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.3 Legal Compliance</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Comply with applicable laws, regulations, and legal processes</li>
                <li>Respond to requests from regulatory authorities</li>
                <li>Enforce our Terms of Service and other agreements</li>
                <li>Protect our rights, property, and safety</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.4 Service Improvement</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Analyze usage patterns to improve our Services</li>
                <li>Develop new features and products</li>
                <li>Conduct research and analytics</li>
                <li>Personalize your experience</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.5 Marketing Communications</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Send promotional materials and newsletters (with your consent)</li>
                <li>Inform you about new features, products, and services</li>
                <li>Conduct surveys and gather feedback</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.6 Lawful Basis for Processing (GDPR)</h3>
              <p>Under the General Data Protection Regulation, we process personal data based on the following legal bases:</p>
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Processing Activity</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Lawful Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Payment processing and settlement</td>
                      <td className="py-3 px-4 text-cyan-400">Performance of contract</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Account management and support</td>
                      <td className="py-3 px-4 text-cyan-400">Performance of contract</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">KYC/AML identity verification</td>
                      <td className="py-3 px-4 text-cyan-400">Legal obligation (PCMLTFA, FINTRAC)</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Fraud detection and prevention</td>
                      <td className="py-3 px-4 text-cyan-400">Legitimate interest / Legal obligation</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Suspicious transaction reporting</td>
                      <td className="py-3 px-4 text-cyan-400">Legal obligation (PCMLTFA)</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Service analytics and improvement</td>
                      <td className="py-3 px-4 text-cyan-400">Legitimate interest</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Marketing communications</td>
                      <td className="py-3 px-4 text-cyan-400">Consent</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Regulatory record retention</td>
                      <td className="py-3 px-4 text-cyan-400">Legal obligation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Section 3: Data Sharing */}
          <section id="sharing" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-cyan-400" />
              3. How We Share Your Information
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>We may share your information with the following categories of recipients:</p>

              <h3 className="text-lg font-medium text-foreground">3.1 Service Providers</h3>
              <p>
                We share information with third-party service providers who perform services on our behalf, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payment processors and acquiring banks</li>
                <li>Cloud hosting and infrastructure providers</li>
                <li>Identity verification and fraud prevention services</li>
                <li>Customer support and communication platforms</li>
                <li>Analytics and monitoring services</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.2 Payment Networks and Financial Institutions</h3>
              <p>
                To process transactions, we share necessary information with payment card networks (Visa, Mastercard, etc.), banks, and other financial institutions involved in the payment chain.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">3.3 Legal and Regulatory Authorities</h3>
              <p>
                We may disclose your information when required by law, regulation, legal process, or governmental request, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Responding to subpoenas, court orders, or other legal processes</li>
                <li>Cooperating with law enforcement investigations</li>
                <li>Reporting suspicious activity to financial intelligence units</li>
                <li>Complying with regulatory examinations and audits</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.4 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections described in this policy.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">3.5 With Your Consent</h3>
              <p>
                We may share your information with third parties when you have given us explicit consent to do so.
              </p>

              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mt-6">
                <p className="text-amber-200 text-sm">
                  <strong>We Do Not Sell Your Data:</strong> FluxPay does not sell, rent, or trade your personal information to third parties for their marketing purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Data Security */}
          <section id="security" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-cyan-400" />
              4. Data Security
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                FluxPay implements comprehensive security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>

              <h3 className="text-lg font-medium text-foreground">4.1 Security Practices</h3>
              <p>
                We implement industry-standard security practices including encryption of data in transit and at rest, access controls, and regular security assessments to protect your personal information.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">4.2 Technical Safeguards</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>256-bit TLS encryption for all data in transit</li>
                <li>AES-256 encryption for sensitive data at rest</li>
                <li>Multi-factor authentication for account access</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Intrusion detection and prevention systems</li>
                <li>24/7 security monitoring and incident response</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.3 Organizational Measures</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Strict access controls based on least privilege principle</li>
                <li>Regular employee security training and awareness programs</li>
                <li>Background checks for employees with data access</li>
                <li>Documented security policies and procedures</li>
                <li>Incident response and breach notification procedures</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.4 Data Breach Notification</h3>
              <p>In the event of a personal data breach that poses a risk to your rights and freedoms, FluxPay will:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>GDPR (EEA):</strong> Notify the relevant supervisory authority within <strong>72 hours</strong> of becoming aware of the breach, and notify affected individuals without undue delay where the breach poses a high risk</li>
                <li><strong>PIPEDA (Canada):</strong> Report to the Office of the Privacy Commissioner and notify affected individuals <strong>as soon as feasible</strong> after determining that a real risk of significant harm exists</li>
                <li><strong>CCPA (California):</strong> Provide notice to affected California residents in compliance with California Civil Code Section 1798.82</li>
              </ul>
              <p className="mt-2">
                Breach notifications will include: a description of the breach, the types of data affected, likely consequences, measures taken to address the breach, and recommendations for affected individuals.
              </p>
            </div>
          </section>

          {/* Section 5: Data Retention */}
          <section id="retention" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Server className="w-5 h-5 text-cyan-400" />
              5. Data Retention
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, comply with our legal obligations, resolve disputes, and enforce our agreements.
              </p>

              <h3 className="text-lg font-medium text-foreground">5.1 Retention Periods</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Data Type</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Account Information</td>
                      <td className="py-3 px-4">Duration of account + 7 years</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Transaction Records</td>
                      <td className="py-3 px-4">7 years (regulatory requirement)</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">KYC/AML Documentation</td>
                      <td className="py-3 px-4">5-7 years after relationship ends</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Support Communications</td>
                      <td className="py-3 px-4">3 years</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Marketing Preferences</td>
                      <td className="py-3 px-4">Until consent withdrawn</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4">Usage Analytics</td>
                      <td className="py-3 px-4">2 years (anonymized thereafter)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">5.2 Data Deletion</h3>
              <p>
                Upon account termination or upon your request (subject to legal retention requirements), we will delete or anonymize your personal information within 90 days, except where we are required to retain it for legal, regulatory, or legitimate business purposes.
              </p>
            </div>
          </section>

          {/* Section 6: Your Rights */}
          <section id="rights" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-cyan-400" />
              6. Your Privacy Rights
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Depending on your location and applicable laws, you may have the following rights regarding your personal information:
              </p>

              <h3 className="text-lg font-medium text-foreground">6.1 GDPR Rights (EEA Residents)</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with a supervisory authority</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">6.2 CCPA Rights (California Residents)</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right to Know:</strong> Request information about data collection and sharing</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (we do not sell data)</li>
                <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">6.3 PIPEDA Rights (Canadian Residents)</h3>
              <p>Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right of Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw your consent to the collection, use, or disclosure of your personal information, subject to legal or contractual restrictions</li>
                <li><strong>Right to Complain:</strong> File a complaint with the Office of the Privacy Commissioner of Canada (OPC) if you believe your privacy rights have been violated</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">6.4 Exercising Your Rights</h3>
              <p>
                To exercise any of these rights, please contact us using the information provided at the end of this policy. We will respond to your request within 30 days (or the timeframe required by applicable law). We may need to verify your identity before processing your request.
              </p>

              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 mt-6">
                <p className="text-cyan-200 text-sm">
                  <strong>Important:</strong> Some rights may be limited where we have a legal obligation to retain data or where data is necessary for fraud prevention and security purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Cookies */}
          <section id="cookies" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              7. Cookies and Tracking Technologies
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We use cookies and similar tracking technologies to collect information about your browsing activities and to provide and improve our Services.
              </p>

              <h3 className="text-lg font-medium text-foreground">7.1 Types of Cookies We Use</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Cookie Type</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Essential</td>
                      <td className="py-3 px-4">Required for the Services to function (authentication, security)</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Functional</td>
                      <td className="py-3 px-4">Remember your preferences and settings</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Analytics</td>
                      <td className="py-3 px-4">Understand how users interact with our Services</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Performance</td>
                      <td className="py-3 px-4">Monitor and improve Service performance</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">7.2 Managing Cookies</h3>
              <p>
                You can control cookies through your browser settings. However, disabling certain cookies may affect the functionality of our Services. Most browsers allow you to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>View and delete existing cookies</li>
                <li>Block all cookies or third-party cookies</li>
                <li>Set preferences for certain websites</li>
                <li>Receive alerts when cookies are being set</li>
              </ul>
            </div>
          </section>

          {/* Section 8: International Transfers */}
          <section id="transfers" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              8. International Data Transfers
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                FluxPay operates globally and may transfer your personal information to countries other than your country of residence. These countries may have data protection laws that differ from those in your jurisdiction.
              </p>

              <h3 className="text-lg font-medium text-foreground">8.1 Transfer Safeguards</h3>
              <p>When we transfer personal data internationally, we implement appropriate safeguards including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Data Processing Agreements with all service providers</li>
                <li>Adequacy decisions where applicable</li>
                <li>Binding Corporate Rules for intra-group transfers</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">8.2 Data Storage Locations</h3>
              <p>
                Primary data processing occurs in secure data centers located in the United States and European Union. We use geographically distributed infrastructure to ensure reliability and compliance with regional data residency requirements where applicable.
              </p>
            </div>
          </section>

          {/* Section 9: Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">9. Children's Privacy</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Our Services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete that information promptly.
              </p>
            </div>
          </section>

          {/* Section 10: Updates */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-cyan-400" />
              10. Updates to This Policy
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make material changes, we will:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Update the "Last Updated" date at the top of this policy</li>
                <li>Notify you via email or through the Services</li>
                <li>Provide at least 30 days notice before material changes take effect</li>
              </ul>
              <p>
                We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              Contact Us
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:</p>

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="p-4 rounded-xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
                  <h4 className="text-foreground font-medium mb-2">Data Protection Officer</h4>
                  <p className="text-sm">FluxPay — Ideation House Financial Services Limited</p>
                  <p className="text-sm">3080 Yonge Street, Suite 6060, Toronto, Ontario, M4N 3N1, Canada</p>
                  <p className="text-sm">Email: privacy@fluxpay.online</p>
                </div>
                <div className="p-4 rounded-xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
                  <h4 className="text-foreground font-medium mb-2">Privacy Inquiries</h4>
                  <p className="text-sm">Email: privacy@fluxpay.online</p>
                  <p className="text-sm">Support: support@fluxpay.online</p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 mt-4">
                <p className="text-cyan-200 text-sm">
                  <strong>EU Representative:</strong> For GDPR inquiries from EU residents, you may also contact our EU representative at eu-representative@fluxpay.online
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="pt-8 border-t border-border dark:border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <Link href="/terms" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-600">·</span>
            <Link href="/aml-policy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              AML/KYC Policy
            </Link>
            <span className="text-gray-600">·</span>
            <Link href="/refund-policy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Refund Policy
            </Link>
          </div>
          <div className="flex justify-end">
            <Link href="/auth/signin" className="px-6 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              Back to Sign In
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
